import { config } from '@/config';
import { buildSerpOrSokLink } from '@/features/mega-menu/helpers';
import SearchResultsRoute from '@/features/unified-search/SearchResultsRoute';
import { isSistaminuten } from '@/helpers';
import { getFaq, getSupportFaq } from '@/helpers/faq';
import { DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION } from '@/hooks/search/useSearch.constants';
import { searchTypeSchema } from '@/hooks/search/useSearch.helpers';
import {
  About,
  AddReview,
  AppointmentDetails,
  Appointments,
  AppsDownload,
  BlogArchive,
  BlogCategory,
  BlogHome,
  BlogSinglePost,
  BookingCalendar,
  BookingCheckout,
  BookingFlowSteps,
  BundleCheckout,
  BundleConfirmation,
  BundleDetails,
  Bundles,
  Cancel,
  Cookies,
  FAQ,
  Favorites,
  FinishAccount,
  GenerateBanner,
  GiftcardCheckout,
  HomepageSupport,
  GiftcardConfirmation,
  Home,
  InjectionNotice,
  KlarnaCheckout,
  LandingPage,
  LandingPageArchive,
  Newsletter,
  NotFound,
  PaymentMethods,
  PlaceRedirect,
  Portal,
  PrepayCheckout,
  PrePayKlarnaCheckout,
  PrePayQliroCheckout,
  QliroBundleCheckout,
  QliroCheckout,
  ReviewRedirect,
  SalonLanding,
  SearchNotice,
  SecurityAcknowledgements,
  SecurityPolicy,
  ServicesAtHome,
  Support,
  SupportForm,
  SupportFormSuccess,
  SupportRemindersLog,
  SupportReviews,
  SupportReviewsUsers,
  SupportSection,
  Unsubscribe,
  UnsubscribeFromReviews,
  ValidateCofPaymentRedirect,
  ValidateQliroPayment,
  ValidateSwishPaymentRedirect,
  ValidateSwishRedirect,
  ValidateThreeDSecureRedirect,
  ValueCardsBuy,
  WaitingListSuccess,
} from '@/pages/_exports';

import ValidateBokadirektPay from '@/features/payments/components/validate-bokadirekt-pay/ValidateBokadirektPay';
import AddGiftcard from '@/pages/add-giftcard/AddGiftcard';
import Confirmation from '@/pages/booking/confirmation/BookingConfirmation';
import AssociationsArchive from '@/pages/bransch-organisationer';
import AssociationsLanding from '@/pages/bransch-organisationer/$slug';
import DealsLandingPage from '@/pages/deals';
import DealsDetailsPage from '@/pages/deals/$slug';
import GiftcardsPage from '@/pages/giftcards';
import MyProfile from '@/pages/my-profile/MyProfile';
import QliroGiftcardCheckout from '@/pages/presentkort/qliro/QliroGiftcardCheckout';
import MobileSearchPage from '@/pages/search';
import { Redirect } from 'react-router-dom';
import { APP_ROUTES } from './pages';

const getFaqPaths = (): string[] => [APP_ROUTES.FAQ, ...getFaq().map((section) => section.path)];
const getSupportPaths = (): string[] => [...getSupportFaq().map((section) => section.path)];

export type AppRoute = {
  exact?: boolean;
  path?: string | string[];
  component: any;
  allowOnSistaminuten?: boolean;
  /** Routes sharing a mount key keep their component mounted when navigating between them. */
  mountKey?: string;
};

const SEARCH_RESULTS_MOUNT_KEY = 'search-results';

export const getAppRoutes = (isMobileView: boolean): AppRoute[] =>
  [
    ...(isMobileView
      ? [{ path: APP_ROUTES.MOBILE_SEARCH, component: MobileSearchPage, allowOnSistaminuten: true }]
      : [
          {
            path: APP_ROUTES.MOBILE_SEARCH,
            allowOnSistaminuten: true,
            component: ({ location }) => {
              const parsedSearchType = searchTypeSchema.safeParse(location?.state?.searchType);
              const searchType = parsedSearchType.success ? parsedSearchType.data : 'default';

              const state = {
                keyword: location?.state?.q || DEFAULT_SEARCH_KEYWORD,
                location: location?.state?.location || DEFAULT_SEARCH_LOCATION,
                searchType,
              };

              const { pathname, searchString } = buildSerpOrSokLink(
                state.keyword,
                state.location,
                state.searchType,
                location?.search,
              );

              const redirect = { pathname, search: searchString };
              return <Redirect to={redirect} />;
            },
          },
        ]),
    { exact: true, path: APP_ROUTES.HOME, component: Home, allowOnSistaminuten: true },
    {
      exact: true,
      path: '/calle',
      component: (props) => {
        window.location.href =
          'https://www.bokadirekt.se/presentkort?utm_source=schulman&utm_medium=podcast&utm_campaign=inflation&utm_id=schulman';
        return <span>redirecting...</span>;
      },
    },
    {
      exact: true,
      path: APP_ROUTES.APP,
      component: AppsDownload,
    },
    {
      exact: true,
      path: '/fredagspodden',
      component: (props) => {
        window.location.href =
          'https://www.bokadirekt.se/presentkort?utm_source=podcast&utm_medium=fredagspodden&utm_campaign=fredagspodden_221003';
        return <span>redirecting...</span>;
      },
    },
    {
      exact: true,
      path: '/alexochsigge',
      component: (props) => {
        window.location.href =
          'https://www.bokadirekt.se/presentkort?utm_source=podcast&utm_medium=alexochsigge&utm_campaign=alexochsigge_vecka50_2022';
        return <span>redirecting...</span>;
      },
    },
    {
      path: APP_ROUTES.MY_PROFILE,
      component: MyProfile,
      allowOnSistaminuten: false,
    },

    { path: APP_ROUTES.PLACE_GIFTCARD_CHECKOUT_QLIRO, component: QliroGiftcardCheckout },
    { path: APP_ROUTES.PLACE_GIFTCARD_CHECKOUT, component: GiftcardCheckout },
    { path: APP_ROUTES.PLACE_VALUECARD_CHECKOUT_QLIRO, component: QliroGiftcardCheckout },
    { path: APP_ROUTES.PLACE_VALUECARD_CHECKOUT, component: GiftcardCheckout },
    { path: APP_ROUTES.PLACE_VALUECARD_CONFIRMATION, component: GiftcardConfirmation },
    { path: APP_ROUTES.PLACE_PLACECARD_CONFIRMATION, component: GiftcardConfirmation },
    { path: APP_ROUTES.PLACE_VALUECARD_BUY, component: ValueCardsBuy },
    { path: APP_ROUTES.PLACE_PAGE, component: SalonLanding, allowOnSistaminuten: true },
    { path: APP_ROUTES.PROFILE_PLACE_PAGE, component: SalonLanding, allowOnSistaminuten: false },
    { path: APP_ROUTES.PORTAL, component: Portal },
    { path: APP_ROUTES.GOTO, component: PlaceRedirect },
    { path: APP_ROUTES.BOOKING_CALENDAR, component: BookingCalendar, allowOnSistaminuten: true },
    {
      exact: true,
      path: APP_ROUTES.BOOKING_CHECKOUT_VALIDATE_3DS,
      component: ValidateThreeDSecureRedirect,
      allowOnSistaminuten: true,
    },
    {
      exact: true,
      path: APP_ROUTES.BOOKING_CHECKOUT_VALIDATE_SWISH,
      component: ValidateSwishRedirect,
      allowOnSistaminuten: true,
    },
    { exact: true, path: APP_ROUTES.BOOKING_CHECKOUT_KLARNA, component: KlarnaCheckout, allowOnSistaminuten: true },
    { exact: true, path: APP_ROUTES.BOOKING_CHECKOUT_QLIRO, component: QliroCheckout, allowOnSistaminuten: true },
    { exact: true, path: APP_ROUTES.BOOKING_PREPAY_CHECKOUT, component: PrepayCheckout, allowOnSistaminuten: false },
    {
      exact: true,
      path: APP_ROUTES.BOOKING_PREPAY_CHECKOUT_KLARNA,
      component: PrePayKlarnaCheckout,
      allowOnSistaminuten: false,
    },
    {
      exact: true,
      path: APP_ROUTES.BOOKING_PREPAY_CHECKOUT_QLIRO,
      component: PrePayQliroCheckout,
      allowOnSistaminuten: false,
    },
    {
      path: APP_ROUTES.VALIDATE_SWISH_PAYMENT,
      component: ValidateSwishPaymentRedirect,
      allowOnSistaminuten: false,
    },
    {
      path: APP_ROUTES.VALIDATE_BOKADIREKT_PAY,
      component: ValidateBokadirektPay,
      allowOnSistaminuten: false,
    },
    { path: APP_ROUTES.BOOKING_CHECKOUT, component: BookingCheckout, allowOnSistaminuten: true },
    {
      path: APP_ROUTES.VALIDATE_COF_PAYMENT,
      component: ValidateCofPaymentRedirect,
      allowOnSistaminuten: false,
    },
    { path: APP_ROUTES.VALIDATE_QLIRO_PAYMENT, component: ValidateQliroPayment, allowOnSistaminuten: false },
    { exact: true, path: APP_ROUTES.BUNDLE_CHECKOUT_QLIRO, component: QliroBundleCheckout, allowOnSistaminuten: false },
    { path: APP_ROUTES.BUNDLE_CHECKOUT, component: BundleCheckout, allowOnSistaminuten: false },
    { path: APP_ROUTES.BUNDLE_CONFIRMATION, component: BundleConfirmation, allowOnSistaminuten: false },
    { path: APP_ROUTES.MY_BOOKINGS, component: Appointments },
    { exact: true, path: APP_ROUTES.DEAL_DETAILS, component: DealsDetailsPage, allowOnSistaminuten: true },
    { path: APP_ROUTES.DEALS, component: DealsLandingPage },
    { path: APP_ROUTES.MY_BUNDLES_VALID, component: Bundles, allowOnSistaminuten: false },
    { path: APP_ROUTES.MY_BUNDLES_USED, component: Bundles, allowOnSistaminuten: false },
    { path: APP_ROUTES.MY_BUNDLES_EXPIRED, component: Bundles, allowOnSistaminuten: false },
    { path: APP_ROUTES.MY_BUNDLES_DETAILS, component: BundleDetails, allowOnSistaminuten: false },
    { path: APP_ROUTES.MY_BOKNINGS_DETAILS, component: AppointmentDetails, allowOnSistaminuten: true },
    { path: APP_ROUTES.CANCEL_BY_CODE, component: Cancel, allowOnSistaminuten: true },
    { path: APP_ROUTES.MY_FAVORITES, component: Favorites },
    { path: APP_ROUTES.PAYMENT_METHODS, component: PaymentMethods },
    { path: getFaqPaths(), component: FAQ },
    { exact: true, path: APP_ROUTES.BLOG, component: BlogHome },
    { path: APP_ROUTES.BLOG_LIST, component: BlogArchive },
    { path: APP_ROUTES.BLOG_CATEGORY, component: BlogCategory },
    { path: APP_ROUTES.BLOG_SINGLE_POST, component: BlogSinglePost },
    {
      path: APP_ROUTES.GIFTCARD_CONFIRMATION,
      component: GiftcardConfirmation,
      allowOnSistaminuten: false,
    },
    { path: APP_ROUTES.GIFTCARD_CHECKOUT_QLIRO, component: QliroGiftcardCheckout, allowOnSistaminuten: false },
    { path: APP_ROUTES.WELLNESSCARD_CHECKOUT_QLIRO, component: QliroGiftcardCheckout, allowOnSistaminuten: false },
    { path: APP_ROUTES.GIFTCARD_CHECKOUT, component: GiftcardCheckout, allowOnSistaminuten: false },
    { path: APP_ROUTES.WELLNESSCARD_CHECKOUT, component: GiftcardCheckout, allowOnSistaminuten: false },
    { exact: true, path: APP_ROUTES.ADVICE_PAGES_LANDING, component: LandingPage },
    { exact: true, path: APP_ROUTES.ADVICE_PAGES_ARCHIVE, component: LandingPageArchive },
    { path: APP_ROUTES.ASSOCIATION_PAGES_LANDING, component: AssociationsLanding, allowOnSistaminuten: true },
    { path: APP_ROUTES.ASSOCIATION_PAGES_ARCHIVE, component: AssociationsArchive, allowOnSistaminuten: true },
    { path: APP_ROUTES.ADD_GIFTCARD, component: AddGiftcard, allowOnSistaminuten: false },
    { exact: true, path: APP_ROUTES.GIFTCARD_USE, component: GiftcardsPage },
    { path: '/orders/:bookingId/booking-confirmation', component: FinishAccount, allowOnSistaminuten: true },
    { path: APP_ROUTES.COOKIE_POLICY, component: Cookies, allowOnSistaminuten: true },
    { path: APP_ROUTES.INJECTION_NOTICE, component: InjectionNotice, allowOnSistaminuten: true },
    { path: APP_ROUTES.SEARCH_NOTICE, component: SearchNotice, allowOnSistaminuten: true },
    { path: APP_ROUTES.ABOUT, component: About, allowOnSistaminuten: true },
    { path: APP_ROUTES.SECURITY_POLICY, component: SecurityPolicy, allowOnSistaminuten: false },
    { path: APP_ROUTES.SECURITY_ACKNOWLEDGEMENTS, component: SecurityAcknowledgements, allowOnSistaminuten: false },
    { path: APP_ROUTES.REVIEWS_UNSUBSCRIBE, component: UnsubscribeFromReviews, allowOnSistaminuten: false },
    { path: APP_ROUTES.REVIEWS_BY_USER, component: AddReview, allowOnSistaminuten: true },
    { path: APP_ROUTES.REVIEWS_REDIRECT, component: ReviewRedirect, allowOnSistaminuten: true },
    { exact: true, path: APP_ROUTES.SUPPORT, component: Support, allowOnSistaminuten: true },
    { path: APP_ROUTES.SUPPORT_FORM, component: SupportForm, allowOnSistaminuten: true },
    { exact: true, path: APP_ROUTES.BOOKING_CONFIRMATION, component: Confirmation, allowOnSistaminuten: true },
    { path: APP_ROUTES.SUPPORT_FORM_SUBMITTED, component: SupportFormSuccess, allowOnSistaminuten: true },
    ...(config.env !== 'production'
      ? [{ exact: true, path: '/generate-banner', component: GenerateBanner, allowOnSistaminuten: true }]
      : []),

    /* Temporary to prevent broken links from google */
    {
      exact: true,
      path: '/articles/support',
      component: () => {
        return <Redirect to="/support" />;
      },
      allowOnSistaminuten: true,
    },
    {
      path: '/articles/support/form',
      component: () => {
        return <Redirect to="/support/form" />;
      },
      allowOnSistaminuten: true,
    },
    { path: getSupportPaths(), component: SupportSection, allowOnSistaminuten: true },
    { path: APP_ROUTES.NEWSLETTER, component: Newsletter },
    { path: APP_ROUTES.WAITING_LIST_SUCCESS, component: WaitingListSuccess },
    { path: APP_ROUTES.UNSUBSCRIBE, component: Unsubscribe, allowOnSistaminuten: true },
    { path: APP_ROUTES.SERVICES_AT_HOME, component: ServicesAtHome },
    {
      path: [
        APP_ROUTES.SISTA_MINUTEN_SERP_NO_INDEX,
        APP_ROUTES.DEALS_SERP_NO_INDEX,
        APP_ROUTES.DEALS_SERP_PREVIEW_NO_INDEX,
        APP_ROUTES.BRAND_DEALS_SERP_NO_INDEX,
        APP_ROUTES.DEALS_SERP,
        APP_ROUTES.SISTA_MINUTEN_SERP,
        APP_ROUTES.DEALS_SERP_PREVIEW,
        APP_ROUTES.BRAND_DEALS_SERP,
      ],
      component: SearchResultsRoute,
      mountKey: SEARCH_RESULTS_MOUNT_KEY,
      allowOnSistaminuten: true,
    },
    /* Booking flow step pages — one Route (array path, like SERP below) so the shared
       BookingFlowSteps parent stays mounted across step transitions (R15 focus).
       Must stay above the SERP entry so /boka/... is never read as a search. */
    {
      exact: true,
      path: [
        APP_ROUTES.BOOKING_FLOW_SELECT_SERVICE,
        APP_ROUTES.BOOKING_FLOW_SELECT_EMPLOYEE,
        APP_ROUTES.BOOKING_FLOW_ADD_ONS,
        APP_ROUTES.BOOKING_FLOW_EXTRA_TIME,
      ],
      component: BookingFlowSteps,
      allowOnSistaminuten: true,
    },
    {
      exact: true,
      path: [APP_ROUTES.SERP_NO_INDEX, APP_ROUTES.SERP],
      component: SearchResultsRoute,
      mountKey: SEARCH_RESULTS_MOUNT_KEY,
      allowOnSistaminuten: true,
    },
    { path: APP_ROUTES.REVIEWS_SUPPORT, component: SupportReviews, allowOnSistaminuten: false },
    { path: APP_ROUTES.REVIEWS_SUPPORT_USERS, component: SupportReviewsUsers, allowOnSistaminuten: false },
    { path: APP_ROUTES.REVIEWS_SUPPORT_REMINDERS, component: SupportRemindersLog, allowOnSistaminuten: false },
    { path: APP_ROUTES.HOMEPAGE_SUPPORT, component: HomepageSupport, allowOnSistaminuten: false },
    { component: NotFound, allowOnSistaminuten: true },
  ].filter((route) => (isSistaminuten() ? route.allowOnSistaminuten ?? false : true));
